/**
 * #844: the session record is created before its engine. If the engine create fails, remove the
 * just-created record so no active 'initializing' orphan (no deployment) is left behind — but ONLY
 * when that cannot orphan a live engine. A timeout / network error / 5xx / 409 may have happened
 * AFTER the backend applied the manifests, so deleting the doc would strand a running Deployment
 * (pod boot 404). Those cases roll back only once check-resources confirms nothing was created.
 */

/** HTTP statuses that are NOT a definite pre-apply rejection even though they are 4xx. */
const AMBIGUOUS_4XX = new Set([408, 409, 429]);

/**
 * True only for a definite server rejection: an HTTP error response with a 4xx status (RTK Query
 * `{ status: number }`). FETCH_ERROR / TIMEOUT_ERROR / PARSING_ERROR (string status), 5xx, and the
 * ambiguous 4xx above are not definite.
 */
export function isDefiniteRejection(error: unknown): boolean {
  if (typeof error !== 'object' || error === null || !('status' in error)) return false;
  const { status } = error;
  return typeof status === 'number' && status >= 400 && status < 500 && !AMBIGUOUS_4XX.has(status);
}

export async function startEngineOrRollback(deps: {
  startEngine: () => Promise<unknown>;
  removeSession: () => Promise<unknown>;
  /** Resolves true only when check-resources positively reports the session has no deployment. */
  confirmNoDeployment: () => Promise<boolean>;
  onRolledBack?: () => Promise<unknown>;
}): Promise<void> {
  try {
    await deps.startEngine();
  } catch (engineError) {
    let safeToRollBack = isDefiniteRejection(engineError);
    if (!safeToRollBack) {
      try {
        safeToRollBack = await deps.confirmNoDeployment();
      } catch (checkError) {
        console.error('Could not confirm engine absence; keeping the session record', checkError);
      }
    }
    if (safeToRollBack) {
      try {
        await deps.removeSession();
      } catch (removeError) {
        console.error('Failed to roll back session after engine create failure', removeError);
      }
      await deps.onRolledBack?.();
    }
    throw engineError;
  }
}

interface ResourceCheckEntry { sessionId: string; deploymentPresent: boolean; skipped: boolean }

/**
 * True only for a fresh (not skipped/cached) probe of THIS session that reports its DEPLOYMENT absent
 * (`deploymentPresent === false`). `resourcesMissing` is deliberately not used: it is true when only
 * the PVC is gone, which says nothing about a running Deployment. A response without the field
 * (older backend) never confirms.
 */
export function confirmsNoDeployment(result: unknown, sessionId: string): boolean {
  if (typeof result !== 'object' || result === null || !('resourceChecks' in result) || !Array.isArray(result.resourceChecks)) return false;
  const entries: unknown[] = result.resourceChecks;
  return entries.some((e): e is ResourceCheckEntry =>
    typeof e === 'object' && e !== null
    && 'sessionId' in e && e.sessionId === sessionId
    && 'deploymentPresent' in e && e.deploymentPresent === false
    && 'skipped' in e && e.skipped === false);
}
